import { useEffect, useRef } from 'react';

/**
 * Registers a document-level copy event listener that copies
 * custom-highlighted text (Delve's paragraph selection) to the clipboard.
 *
 * If the user has a native browser selection, the browser handles copy normally.
 * Otherwise, we use the custom selection state (start/end) with getSubstring
 * to put the highlighted text on the clipboard.
 *
 * @param {Object} select - The current selection state ({ start, end, ... })
 * @param {Function} getSubstring - Function that extracts text given (start, end)
 */
const useCopyHandler = (select, getSubstring) => {
  const selectRef = useRef(select);

  useEffect(() => {
    selectRef.current = select;
  }, [select]);

  useEffect(() => {
    const copyHandler = (e) => {
      const browserSelection = window.getSelection();
      const hasNativeSelection = browserSelection && browserSelection.toString().length > 0;

      if (hasNativeSelection) {
        return;
      }

      const sel = selectRef.current;
      if (sel && sel.start !== undefined && sel.end !== undefined) {
        const selectedText = getSubstring(sel.start, sel.end);
        if (selectedText) {
          e.clipboardData.setData('text/plain', selectedText);
          e.preventDefault();
        }
      }
    };

    document.addEventListener('copy', copyHandler, false);
    return () => {
      document.removeEventListener('copy', copyHandler, false);
    };
  }, [getSubstring]);
};

export default useCopyHandler;
